/**
 * RTK Query slice for the saved-search alerts feature.
 *
 * Surfaces:
 *   - `useFetchSavedSearchesQuery({ page?, limit? })` — paginated list of
 *     the signed-in user's saved searches.
 *   - `useCreateSavedSearchMutation()` — POST + tag invalidation.
 *   - `useUpdateSavedSearchMutation()` — PATCH + tag invalidation.
 *   - `useDeleteSavedSearchMutation()` — DELETE + tag invalidation.
 *
 * All four go through the existing `src/util/api.js` wrappers so the
 * BFF stays the single point of contact with Supabase.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  fetchSavedSearches as apiFetchSavedSearches,
  createSavedSearch as apiCreateSavedSearch,
  updateSavedSearch as apiUpdateSavedSearch,
  deleteSavedSearch as apiDeleteSavedSearch,
} from '../util/api';
import * as log from '../util/log';

// ── Domain consts ───────────────────────────────────────────────────────────

/** Allowed delivery channels (FE branches on these strings). */
export const SAVED_SEARCH_CHANNELS = ['email', 'push', 'sms'] as const;
export type SavedSearchChannel = (typeof SAVED_SEARCH_CHANNELS)[number];

/** Allowed alert frequencies. */
export const SAVED_SEARCH_FREQUENCIES = ['instant', 'daily', 'weekly'] as const;
export type SavedSearchFrequency = (typeof SAVED_SEARCH_FREQUENCIES)[number];

/** Stable error codes the FE branches on / shows translated messages for. */
export const SAVED_SEARCH_ERROR = {
  ListFailed: 'SAVED_SEARCH_LIST_FAILED',
  CreateFailed: 'SAVED_SEARCH_CREATE_FAILED',
  UpdateFailed: 'SAVED_SEARCH_UPDATE_FAILED',
  DeleteFailed: 'SAVED_SEARCH_DELETE_FAILED',
} as const;
export type SavedSearchError = (typeof SAVED_SEARCH_ERROR)[keyof typeof SAVED_SEARCH_ERROR];

// ── Wire shapes ─────────────────────────────────────────────────────────────

/** A single saved-search row as returned by the BFF. */
export type SavedSearch = {
  id: string;
  label: string | null;
  query_text: string;
  filters_json: Record<string, unknown>;
  location_box: string | null;
  category_filter: string[] | null;
  channels: SavedSearchChannel[];
  frequency: SavedSearchFrequency;
  created_at: string;
  last_notified_at: string | null;
};

/** Partial payload accepted by POST and PATCH (PATCH allows omissions). */
export type SavedSearchPayload = {
  label?: string | null;
  query_text?: string;
  filters_json?: Record<string, unknown>;
  location_box?: string | null;
  category_filter?: string[] | null;
  channels?: SavedSearchChannel[];
  frequency?: SavedSearchFrequency;
};

type Pagination = {
  page: number;
  perPage: number;
  total: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

export type SavedSearchListResponse = {
  items: SavedSearch[];
  pagination: Pagination | null;
};

type ListArgs = { page?: number; limit?: number };

// ── Helpers ─────────────────────────────────────────────────────────────────

const toRtkError = (e: unknown, code: SavedSearchError) => {
  log.error(
    e instanceof Error ? e : new Error(String(e)),
    'saved-searches.api.failed',
    { code }
  );
  return {
    error: { status: (e as { status?: number })?.status || 500, data: { code } },
  };
};

// ── Slice ───────────────────────────────────────────────────────────────────

export const savedSearchesApi = createApi({
  reducerPath: 'savedSearchesApi',
  baseQuery: () => ({ data: null }),
  tagTypes: ['SavedSearches'],
  endpoints: build => ({
    fetchSavedSearches: build.query<SavedSearchListResponse, ListArgs | void>({
      providesTags: ['SavedSearches'],
      async queryFn(args) {
        try {
          const { page = 1, limit = 50 } = args || {};
          const data = (await apiFetchSavedSearches(page, limit)) as SavedSearchListResponse;
          return { data };
        } catch (e) {
          return toRtkError(e, SAVED_SEARCH_ERROR.ListFailed);
        }
      },
    }),
    createSavedSearch: build.mutation<SavedSearch, SavedSearchPayload>({
      invalidatesTags: ['SavedSearches'],
      async queryFn(payload) {
        try {
          const data = (await apiCreateSavedSearch(payload)) as SavedSearch;
          return { data };
        } catch (e) {
          return toRtkError(e, SAVED_SEARCH_ERROR.CreateFailed);
        }
      },
    }),
    updateSavedSearch: build.mutation<
      SavedSearch,
      { id: string; patch: SavedSearchPayload }
    >({
      invalidatesTags: ['SavedSearches'],
      async queryFn({ id, patch }) {
        try {
          const data = (await apiUpdateSavedSearch(id, patch)) as SavedSearch;
          return { data };
        } catch (e) {
          return toRtkError(e, SAVED_SEARCH_ERROR.UpdateFailed);
        }
      },
    }),
    deleteSavedSearch: build.mutation<{ id: string }, string>({
      invalidatesTags: ['SavedSearches'],
      async queryFn(id) {
        try {
          await apiDeleteSavedSearch(id);
          return { data: { id } };
        } catch (e) {
          return toRtkError(e, SAVED_SEARCH_ERROR.DeleteFailed);
        }
      },
    }),
  }),
});

export const {
  useFetchSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useUpdateSavedSearchMutation,
  useDeleteSavedSearchMutation,
} = savedSearchesApi;

export default savedSearchesApi.reducer;
